/**
 * Pure path utility functions — no platform dependency.
 *
 * These are simple string operations that work identically across
 * all platforms (Editor/Web/Play).
 */

/** Extract file name from a full path (supports both / and \ separators) */
export function fileNameFromPath(filePath: string): string {
    return filePath.split(/[/\\]/).pop() ?? filePath;
}

/** Extract parent directory from a full path. */
export function dirNameFromPath(filePath: string): string {
    const normalized = filePath.replace(/\\/g, "/");
    const idx = normalized.lastIndexOf("/");
    if (idx < 0) return "";
    if (idx === 0) return "/";
    return normalized.slice(0, idx);
}

export type PathBreadcrumbSegmentKind =
    | "root"
    | "folder"
    | "file"
    | "ellipsis";

export interface PathBreadcrumbSegment {
    label: string;
    path: string;
    kind: PathBreadcrumbSegmentKind;
}

interface WorkspaceRootLike {
    path: string;
    name?: string | null;
}

function normalizeSlashPath(path: string): string {
    if (path === "") return "";
    const normalized = path.replace(/\\/g, "/").replace(/\/+$/, "");
    return normalized || "/";
}

function joinSlashPath(base: string, segment: string): string {
    if (base === "/" || base === "") return `${base === "/" ? "/" : ""}${segment}`;
    return `${base}/${segment}`;
}

function findContainingRoot(
    filePath: string,
    workspaceRoots: readonly WorkspaceRootLike[],
): WorkspaceRootLike | null {
    const normalizedFilePath = normalizeSlashPath(filePath);
    return workspaceRoots
        .filter((root) => {
            const normalizedRoot = normalizeSlashPath(root.path);
            const boundary = normalizedRoot === "/" ? "/" : `${normalizedRoot}/`;
            return (
                normalizedFilePath === normalizedRoot ||
                normalizedFilePath.startsWith(boundary)
            );
        })
        .sort(
            (a, b) =>
                normalizeSlashPath(b.path).length - normalizeSlashPath(a.path).length,
        )[0] ?? null;
}

export function buildPathBreadcrumbSegments(
    filePath: string,
    workspaceRoots: readonly WorkspaceRootLike[] = [],
): PathBreadcrumbSegment[] {
    const normalizedFilePath = normalizeSlashPath(filePath);
    if (!normalizedFilePath) return [];

    const containingRoot = findContainingRoot(normalizedFilePath, workspaceRoots);
    if (containingRoot) {
        const normalizedRoot = normalizeSlashPath(containingRoot.path);
        const rootLabel =
            containingRoot.name || fileNameFromPath(normalizedRoot) || normalizedRoot;
        const relativePath =
            normalizedFilePath === normalizedRoot
                ? ""
                : normalizedFilePath.slice(normalizedRoot.length).replace(/^\/+/, "");
        const relativeParts = relativePath.split("/").filter(Boolean);
        const segments: PathBreadcrumbSegment[] = [
            { label: rootLabel, path: normalizedRoot, kind: "root" },
        ];
        let currentPath = normalizedRoot;
        relativeParts.forEach((part, index) => {
            currentPath = joinSlashPath(currentPath, part);
            segments.push({
                label: part,
                path: currentPath,
                kind: index === relativeParts.length - 1 ? "file" : "folder",
            });
        });
        return segments;
    }

    const absoluteRoot = normalizedFilePath.startsWith("/") ? "/" : "";
    const parts = normalizedFilePath.split("/").filter(Boolean);
    const segments: PathBreadcrumbSegment[] = [];
    let currentPath = absoluteRoot;
    if (absoluteRoot) {
        segments.push({ label: "/", path: "/", kind: "root" });
    }
    parts.forEach((part, index) => {
        currentPath = joinSlashPath(currentPath, part);
        segments.push({
            label: part,
            path: currentPath,
            kind: index === parts.length - 1 ? "file" : "folder",
        });
    });
    return segments;
}

export function condensePathBreadcrumbSegments(
    segments: readonly PathBreadcrumbSegment[],
    maxSegments = 5,
): PathBreadcrumbSegment[] {
    if (segments.length <= maxSegments) return [...segments];
    if (maxSegments < 3) return segments.slice(-maxSegments);

    const tailCount = maxSegments - 2;
    return [
        segments[0],
        {
            label: "...",
            path: segments[0]?.path ?? "",
            kind: "ellipsis",
        },
        ...segments.slice(-tailCount),
    ];
}

/** Initial directory for document-scoped file/folder pickers. */
export function pickerInitialPathFromDocument(
    filePath?: string | null,
): string | undefined {
    if (!filePath) return undefined;
    return dirNameFromPath(filePath) || undefined;
}

/**
 * Strip shell-style backslash escapes from a path.
 *
 * Users often paste terminal paths like `/Users/me/Mobile\ Documents/foo`
 * where `\ ` is a shell escape for a literal space. When passed to FS APIs,
 * the backslash is treated as a literal character, causing "No such file"
 * errors. This function normalizes `\<char>` → `<char>`.
 */
export function normalizeUserPath(input: string): string {
    // Replace backslash followed by any character with just that character.
    // This handles `\ ` (space), `\(`, `\)`, etc.
    return input.replace(/\\(.)/g, "$1");
}

function normalizePathForMatch(value: string): string {
    return value.replace(/[/\\]+/g, "/").replace(/\/+$/, "") || "/";
}

export function isPathWithinRoots(
    candidatePath: string,
    roots: readonly string[],
): boolean {
    const candidate = normalizePathForMatch(candidatePath);
    return roots.some((rootPath) => {
        const root = normalizePathForMatch(rootPath);
        const boundary = root === "/" ? "/" : `${root}/`;
        return candidate === root || candidate.startsWith(boundary);
    });
}
